import axios from "axios";
import { store } from "../redux/store";
import { logOut, updateUserToken } from "../redux/actions/userDataActions";

const apiUrl = process.env.REACT_APP_ADMIN_API_URL;
let apiSdkUrl = process.env.REACT_APP_SDK_API_URL;
let sdkModelApiKey = process.env.REACT_APP_SDK_MODEL_API_KEY;

// Create an Axios instance
const apiClient = axios.create({
  baseURL: apiUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

apiClient.interceptors.request.use(
  (config) => {
    const state = store.getState();
    const token = state.userData.userToken;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      try {
        const state = store.getState();
        const refreshToken = state.userData.refreshToken;

        if (!refreshToken) {
          await store.dispatch(logOut());
          return Promise.reject(error);
        }

        // Call the refresh token endpoint
        const refreshResponse = await axios.post(
          `${apiUrl}auth/refresh`,
          null,
          {
            headers: {
              Authorization: `Bearer ${refreshToken}`,
            },
          }
        );

        const newToken = refreshResponse.data.data.token;
        console.log("++New token received:", newToken);

        // Update the token in the Redux store
        await store.dispatch(updateUserToken(newToken));

        // Update the Authorization header and retry request
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        console.error("++Failed to refresh token:", refreshError);
        await store.dispatch(logOut());
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Create an axios instance for SDK API
export const sdkApiClient = axios.create({
  baseURL: apiSdkUrl,
  headers: {
    "Content-Type": "application/json",
    "X-Api-Key": sdkModelApiKey,
  },
});

// Add a response interceptor
sdkApiClient.interceptors.response.use(
  (response) => response, // Pass through successful responses
  async (error) => {
    const originalRequest = error.config;

    // Check if the error is due to token expiration
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      try {
        // Refresh the token
        const state = store.getState();
        const refreshToken = state.userData.refreshToken;

        const refreshResponse = await axios.post(
          `${process.env.REACT_APP_ADMIN_API_URL}auth/refresh`,
          {},
          {
            headers: {
              Authorization: `Bearer ${refreshToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        const newToken = refreshResponse.data.data.token;

        // Update the token in the store
        await store.dispatch(updateUserToken(newToken));

        // Retry the original request with the new token
        originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
        return sdkApiClient(originalRequest);
      } catch (refreshError) {
        // If refresh fails, log out the user
        await store.dispatch(logOut(""));
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
